import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  UserCircleIcon,
  Bars3Icon,
  HomeIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  BuildingStorefrontIcon,
  Squares2X2Icon,
  CalendarIcon,
  TicketIcon,
} from '@heroicons/react/24/outline';
import { getToken } from '../../utils/constants';
import useAuth from '../../hooks/useAuth';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { DASHBOARD_URL } from '../../config/appConfig';
import { logout } from '../../features/auth/authSlice';
import Sidebar from './Sidebar';
import { useToast } from '../common/Toast';

const Header: React.FC = () => {
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const isLoggedIn = !!getToken();
  const toast = useToast();

  // Check if user is already a provider
  const isProvider = user?.roles?.includes('provider_admin') ||
    user?.roles?.includes('provider_staff') ||
    (user?.providerMemberships?.length ?? 0) > 0;
  const dashboardUrl = DASHBOARD_URL;

  // Helper function to check if a route is active
  const isActiveRoute = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };

  const handleLogout = () => {
    dispatch(logout());
    setIsProfileMenuOpen(false);
    toast.success("You've been logged out successfully.");
    navigate('/login');
  };

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };

    if (isProfileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileMenuOpen]);

  const getUserInitial = () => {
    if (user?.name) {
      return user.name.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      {/* Sidebar for mobile */}
      <Sidebar open={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className='flex items-center space-x-6'>
            <Link to="/" className="flex items-center">
              <img
                src="/appointigo.png"
                alt="Appointigo"
                className="h-8 sm:h-10 w-auto object-contain"
              />
            </Link>
            <Link
              to="/providers"
              aria-current={isActiveRoute('/providers') ? 'page' : undefined}
              className={`hidden md:flex font-medium transition-colors ${isActiveRoute('/providers')
                ? 'text-primary-600 border-b-2 border-primary-600 pb-1'
                : 'text-gray-700 hover:text-primary-600'
                }`}
            >
              Providers
            </Link>
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-4">
            <Link
              to="/booking-lookup"
              aria-current={isActiveRoute('/booking-lookup') || isActiveRoute('/track-booking') ? 'page' : undefined}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${(isActiveRoute('/booking-lookup') || isActiveRoute('/track-booking'))
                ? 'bg-primary-50 text-primary-600'
                : 'text-gray-700 hover:bg-gray-100'
                }`}
            >
              <TicketIcon className="w-5 h-5" />
              <span className="font-medium">Track Booking</span>
            </Link>
            {/* <Link
              to="/providers"
              className={`font-medium transition-colors ${isActiveRoute('/providers')
                ? 'text-primary-600 border-b-2 border-primary-600 pb-1'
                : 'text-gray-700 hover:text-primary-600'
                }`}
            >
              Providers
            </Link> */}
            {isLoggedIn && (
              <Link
                to="/bookings"
                aria-current={isActiveRoute('/bookings') ? 'page' : undefined}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${isActiveRoute('/bookings')
                  ? 'bg-primary-50 text-primary-600'
                  : 'text-gray-700 hover:bg-gray-100'
                  }`}
              >
                <CalendarIcon className="w-5 h-5" />
                <span className="font-medium">My Bookings</span>
              </Link>
            )}
            {/* Sign Up button - only for non-logged in users */}
            {!isLoggedIn && (
            //   <Link
            //   to="/register"
            //   className="flex items-center space-x-2 px-6 py-2 border-2 border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
            // >
            //   <span className="font-medium">Sign Up</span>
            // </Link>
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 px-6 py-2 bg-white text-gray-700 font-semibold rounded-lg border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all"
              >
                Sign Up
              </Link>
            )}
            {/* List your business - only for non-providers */}
            {!isProvider && (
              <Link
                to="/list-a-business"
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${isActiveRoute('/list-a-business')
                  ? 'bg-accent-100 text-accent-700'
                  : 'text-accent-600 hover:bg-accent-50 border border-accent-200'
                  }`}
              >
                <BuildingStorefrontIcon className="w-5 h-5" />
                <span className="font-medium">List your business</span>
              </Link>
            )}
            {isLoggedIn ? (
              <div className="relative" ref={profileMenuRef}>
                <button
                  onClick={toggleProfileMenu}
                  aria-expanded={isProfileMenuOpen}
                  aria-haspopup="true"
                  className={`w-11 h-11 rounded-full bg-gradient-to-br flex items-center justify-center text-white font-semibold hover:shadow-lg transition-shadow focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${user?.profile?.avatar ? '' : 'from-primary-600 to-accent-600'}`}
                  aria-label="Profile menu"
                >
                  {user?.profile?.avatar ? (
                    <img
                      src={user?.profile?.avatar}
                      alt={user?.name}
                      className="w-11 h-11 rounded-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    getUserInitial()
                  )}

                </button>

                {/* Profile Dropdown Menu */}
                {isProfileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    {/* User Info */}
                    <div className="px-4 py-3 border-b border-gray-200">
                      <div className="flex items-center space-x-3">
                        <div className={`w-12 h-12 rounded-full bg-gradient-to-br flex items-center justify-center text-white font-bold text-lg${user?.profile?.avatar ? '' : ' from-primary-600 to-accent-600'}`}>
                          {user?.profile?.avatar ? (
                            <img
                              src={user?.profile?.avatar}
                              alt={user?.name}
                              className="w-12 h-12 rounded-full object-cover"
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            getUserInitial()
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {user?.name || 'User'}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {user?.email || 'user@example.com'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Menu Items */}
                    <div className="py-1">
                      <button
                        onClick={() => {
                          navigate('/');
                          setIsProfileMenuOpen(false);
                        }}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <HomeIcon className="w-5 h-5 mr-3 text-gray-400" />
                        Home
                      </button>
                      <button
                        onClick={() => {
                          navigate('/profile');
                          setIsProfileMenuOpen(false);
                        }}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <UserCircleIcon className="w-5 h-5 mr-3 text-gray-400" />
                        Profile
                      </button>
                      <button
                        onClick={() => {
                          navigate('/settings');
                          setIsProfileMenuOpen(false);
                        }}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Cog6ToothIcon className="w-5 h-5 mr-3 text-gray-400" />
                        Settings
                      </button>
                    </div>

                    {/* Provider/Business Section */}
                    <div className="border-t border-gray-200 pt-1">
                      {isProvider ? (
                        /* Provider Dashboard Link */
                        <a
                          href={dashboardUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setIsProfileMenuOpen(false)}
                          className="w-full flex items-center px-4 py-2 text-sm text-primary-600 hover:bg-primary-50 transition-colors"
                        >
                          <Squares2X2Icon className="w-5 h-5 mr-3" />
                          Dashboard
                        </a>
                      ) : (
                        /* Become a Provider Link for Customers */
                        <Link
                          to="/list-a-business"
                          onClick={() => setIsProfileMenuOpen(false)}
                          className="w-full flex items-center px-4 py-2 text-sm text-accent-600 hover:bg-accent-50 transition-colors"
                        >
                          <BuildingStorefrontIcon className="w-5 h-5 mr-3" />
                          Become a Provider
                        </Link>
                      )}
                    </div>

                    {/* Logout */}
                    <div className="border-t border-gray-200 pt-1">
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3" />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-primary-600 to-accent-600 text-white rounded-lg hover:shadow-lg transition-shadow"
              >
                <UserCircleIcon className="w-5 h-5" />
                <span className="font-medium">Sign In</span>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="md:hidden p-2.5 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            aria-label="Open sidebar"
          >
            <Bars3Icon className="w-6 h-6" />
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
